import {
	TPgFeeMatrix,
	TPgFeeRule,
	TPgFeeRuleUpdate,
	TPgPaymentMethodType,
} from 'services/api/paymentGateway/types';
import { PG_FRACTION_DECIMALS, PG_MAX_PERCENT } from '../constants';
import {
	countDecimals,
	fractionToPercentInput,
	parseDecimal,
	percentInputToFraction,
} from '../utils';

/** Editable cell of the matrix. `rule` — the saved rule, `null` when there is none. */
export type TFeeCellDraft = {
	serviceId: number | null;
	methodType: TPgPaymentMethodType;
	rule: TPgFeeRule | null;
	/** The cell has (or will have) a rule; `false` — inherits the default / no fee. */
	enabled: boolean;
	percent: string;
	fixedAmount: string;
	isActive: boolean;
};

export type TFeeDrafts = Record<string, TFeeCellDraft>;

export const cellKey = (serviceId: number | null, methodType: TPgPaymentMethodType): string =>
	`${serviceId ?? 'default'}:${methodType}`;

const fromRule = (
	serviceId: number | null,
	methodType: TPgPaymentMethodType,
	rule: TPgFeeRule | undefined,
): TFeeCellDraft => ({
	serviceId,
	methodType,
	rule: rule ?? null,
	enabled: !!rule,
	percent: rule ? fractionToPercentInput(rule.percent) : '',
	fixedAmount: rule ? String(rule.fixedAmount) : '',
	isActive: rule?.isActive ?? true,
});

/** Drafts of every cell of the matrix (Default row and one row per service). */
export const buildDrafts = (matrix: TPgFeeMatrix): TFeeDrafts => {
	const drafts: TFeeDrafts = {};
	matrix.methodTypes.forEach((methodType) => {
		const defaultRule = matrix.defaults.find((r) => r.methodType === methodType);
		drafts[cellKey(null, methodType)] = fromRule(null, methodType, defaultRule);
		matrix.services.forEach((service) => {
			const rule = service.rules.find((r) => r.methodType === methodType);
			drafts[cellKey(service.serviceId, methodType)] = fromRule(
				service.serviceId,
				methodType,
				rule,
			);
		});
	});
	return drafts;
};

/** Validation message of a cell, `null` when it is fine. */
export const validateCell = (cell: TFeeCellDraft): string | null => {
	if (!cell.enabled) return null;
	const percent = parseDecimal(cell.percent);
	if (percent === null) return 'Percent is required';
	if (percent < 0 || percent >= PG_MAX_PERCENT) return `Percent must be 0 ≤ x < ${PG_MAX_PERCENT}`;
	if (countDecimals(cell.percent) > PG_FRACTION_DECIMALS - 2)
		return `At most ${PG_FRACTION_DECIMALS - 2} decimals in percent`;
	if (cell.fixedAmount.trim() !== '') {
		const fixed = parseDecimal(cell.fixedAmount);
		if (fixed === null || fixed < 0) return 'Fixed part must be ≥ 0';
		if (countDecimals(cell.fixedAmount) > 2) return 'At most 2 decimals in the fixed part';
	}
	return null;
};

const fixedOf = (cell: TFeeCellDraft): number => parseDecimal(cell.fixedAmount) ?? 0;

/** Rows of `PUT /api/admin/fees` for the cells that differ from the saved matrix. */
export const buildChanges = (drafts: TFeeDrafts): TPgFeeRuleUpdate[] =>
	Object.values(drafts).flatMap((cell): TPgFeeRuleUpdate[] => {
		const { rule } = cell;
		if (!cell.enabled) {
			return rule ? [{ serviceId: cell.serviceId, methodType: cell.methodType, delete: true }] : [];
		}
		const percent = percentInputToFraction(cell.percent);
		if (percent === null) return [];
		const fixedAmount = fixedOf(cell);
		if (
			rule &&
			rule.percent === percent &&
			rule.fixedAmount === fixedAmount &&
			rule.isActive === cell.isActive
		) {
			return [];
		}
		return [
			{
				serviceId: cell.serviceId,
				methodType: cell.methodType,
				percent,
				fixedAmount,
				isActive: cell.isActive,
			},
		];
	});

/**
 * Rule the gateway would apply with the draft (02 §2.12): an active rule of the service, else the
 * active default rule, else no fee.
 */
export const effectiveDraftRule = (
	drafts: TFeeDrafts,
	serviceId: number | null,
	methodType: TPgPaymentMethodType,
): { percent: number; fixedAmount: number; source: 'service' | 'default' } | null => {
	const pick = (cell: TFeeCellDraft | undefined, source: 'service' | 'default') => {
		if (!cell || !cell.enabled || !cell.isActive || validateCell(cell)) return null;
		const percent = percentInputToFraction(cell.percent);
		return percent === null ? null : { percent, fixedAmount: fixedOf(cell), source };
	};
	return (
		(serviceId !== null ? pick(drafts[cellKey(serviceId, methodType)], 'service') : null) ??
		pick(drafts[cellKey(null, methodType)], 'default')
	);
};
